import { iOrder } from "core/kangarooTypes/iOrder";
import { iPaginationResponse } from "core/kangarooTypes/iPaginationResponse";
import { iPayment } from "core/kangarooTypes/iPayment";
import {
  downloadPaymentsExcel,
  getOrders,
  getPayments,
} from "core/Services/MerchantServices";
import { ArrowDown, ArrowUp, ChevronDown, Download, Eye } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Button from "shared/Button";
import FormInput from "shared/FormInput";
import { USER_ID } from "utils/constant";
import { formatDate } from "utils/DatesUtil";
import { getStatusColors } from "utils/statusColors";
import { useTranslation } from "react-i18next";

const FinancialReports = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [orders, setOrders] = useState<iPaginationResponse<iOrder>>();
  const [payouts, setPayouts] = useState<iPaginationResponse<iPayment>>();
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    dateRange: "",
    sort_field: "",
    sort_dir: "",
    page: 1,
  });

  const {
    register,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: {
      dateRange: "",
    },
  });

  // Add this useEffect to watch for date range changes
  useEffect(() => {
    const dateRange = watch("dateRange");
    setFilters((prev) => ({
      ...prev,
      dateRange,
    }));
    setCurrentPage(1); // Reset page to 1 when filters change

    let amer = ["7_days", "14_days", "1_month"];
  }, [watch("dateRange")]);

  const calculateDateRange = (days: number) => {
    const dateTo = new Date();
    const dateFrom = new Date();
    dateFrom.setDate(dateTo.getDate() - days);

    const formatDate = (date: Date) => {
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };

    return {
      dateFrom: formatDate(dateFrom),
      dateTo: formatDate(dateTo),
    };
  };

  const dateRangeOptions = [
    { value: "7_days", label: "Last 7 Days", ...calculateDateRange(7) },
    { value: "14_days", label: "Last 14 Days", ...calculateDateRange(14) },
    { value: "1_month", label: "Last 1 Month", ...calculateDateRange(30) },
    { value: "3_months", label: "Last 3 Months", ...calculateDateRange(90) },
    { value: "6_months", label: "Last 6 Months", ...calculateDateRange(180) },
  ];

  const handleSort = (field: string) => {
    setFilters((prev) => ({
      ...prev,
      sort_field: field,
      sort_dir:
        prev.sort_field === field && prev.sort_dir === "asc" ? "desc" : "asc",
    }));
  };

  const SortHeader = ({ field, label }: { field: string; label: string }) => {
    const isActive = filters.sort_field === field;
    return (
      <th
        onClick={() => handleSort(field)}
        className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none"
      >
        <div className="flex items-center space-x-1">
          <span>{label}</span>
          <div className="flex flex-col">
            {isActive && filters.sort_dir === "asc" ? (
              <ArrowUp size={14} />
            ) : null}
            {isActive && filters.sort_dir === "desc" ? (
              <ArrowDown size={14} />
            ) : null}
            {!isActive ? (
              <ArrowDown size={14} className="text-gray-300" />
            ) : null}
          </div>
        </div>
      </th>
    );
  };

  const fetchData = async (
    filters?: {
      dateRange?: string;
      dateFrom?: string;
      dateTo?: string;
      sort_field?: string;
      sort_dir?: string;
    },
    page: number = 1
  ) => {
    try {
      setIsLoading(true);
      const userId = await localStorage.getItem(USER_ID);

      // Base params for all requests
      const baseParams = {
        merchant_id: userId!,
        page,
        date_type: "created_at",
      };

      // Add date range params if filter is selected
      const selectedRange = dateRangeOptions.find(
        (option) => option.value === filters?.dateRange
      );
      const dateParams = selectedRange
        ? {
            dateFrom: selectedRange.dateFrom,
            dateTo: selectedRange.dateTo,
          }
        : {};

      // Add sort params if they exist
      const sortParams = filters?.sort_field
        ? {
            sort_field: filters.sort_field,
            sort_dir: filters.sort_dir,
          }
        : {};

      const response = await getOrders(
        {
          ...baseParams,
          ...dateParams,
          ...sortParams,
        },
        page
      );
      const payments = await getPayments();
      // {
      //   ...baseParams,
      //   ...dateParams,
      //   ...sortParams,
      // }
      if (response) {
        setOrders(response);
      }
      setPayouts(payments);
    } catch (error) {
      // Only show error if it's not the initial load
      if (filters || page > 1) {
        toast.error("Failed to load orders.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchData(filters, currentPage);
  }, [filters, currentPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchData(
      {
        dateRange: watch("dateRange"),
      },
      page
    );
  };

  const handleOrderDetails = (id: number) => {
    navigate(`/order-details/:id=${id}?pageTitle=Order Details`);
  };

  const handleViewPaymentDetails = (paymentId: number) => {
    navigate(`/payment/${paymentId}`);
  };

  const navigate = useNavigate();

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row justify-end gap-4">
        <Button
          icon={<Download size={16} />}
          onClick={downloadPaymentsExcel}
          className="w-full sm:w-auto"
        >
          {t("financialReports.export")}
        </Button>
      </div>

      {/* Filter Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div>
          <FormInput
            name="dateRange"
            type="select"
            icon={<ChevronDown size={20} />}
            placeholder={t("financialReports.selectDateRange")}
            register={register}
            errors={errors}
            selectOptions={dateRangeOptions}
          />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {isLoading
          ? [...Array(5)].map((_, index) => (
              <div
                key={index}
                className="bg-white p-4 rounded-lg border border-gray-200"
              >
                <div className="text-gray-600 text-sm mb-2">
                  <Skeleton width={80} />
                </div>
                <div className="text-2xl">
                  <Skeleton width={40} />
                </div>
              </div>
            ))
          : [
              {
                label: t("financialReports.summaryCards.codTotal"),
                value: payouts?.cod,
              },
              {
                label: t("financialReports.summaryCards.deliveryFees"),
                value: payouts?.delevery_fees,
              },
              {
                label: t("financialReports.summaryCards.totalBalance"),
                value: payouts?.total,
              },
              {
                label: t("financialReports.summaryCards.paymentsProcessed"),
                value: payouts?.payments,
              },
              {
                label: t("financialReports.summaryCards.outstandingBalance"),
                value: payouts?.outstanding,
              },
            ].map((card, index) => (
              <div
                key={index}
                className="bg-white p-4 rounded-lg border border-gray-200"
              >
                <div className="text-gray-600 text-sm">{card.label}</div>{" "}
                <div className="text-2xl mt-1">
                  JOD{" "}
                  {(card.value ?? 0).toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </div>
              </div>
            ))}
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        {/* Desktop Table */}
        <table className="w-full hidden md:table">
          <thead className="bg-gray-50 border-b">
            <tr>
              <SortHeader
                field="id"
                label={t("financialReports.table.orderId")}
              />
              <SortHeader
                field="created_at"
                label={t("financialReports.table.date")}
              />
              <SortHeader
                field="price"
                label={t("financialReports.table.amount")}
              />
              <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t("financialReports.table.pickPackFee") || "Pick & Pack"}
              </th>
              <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t("financialReports.table.servicesFee") || "Services"}
              </th>
              <SortHeader
                field="delivery_fee"
                label={t("financialReports.table.deliveryFee")}
              />
              <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t("financialReports.table.grandTotal") || "Grand Total"}
              </th>
              <SortHeader
                field="status"
                label={t("financialReports.table.payoutStatus")}
              />
              <th className="p-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t("financialReports.table.actions")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {isLoading
              ? [...Array(5)].map((_, index) => (
                  <tr key={index}>
                    {[...Array(9)].map((_, colIndex) => (
                      <td key={colIndex} className="p-3">
                        <Skeleton width={80} />
                      </td>
                    ))}
                  </tr>
                ))
              : orders?.data.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="p-3 font-medium">
                      {order.external_order_number || order.id}
                    </td>
                    <td className="p-3">{formatDate(order.created_at)}</td>
                    <td className="p-3">JOD {order.price.toFixed(2)}</td>
                    <td className="p-3">
                      {order.pick_pack_fee !== undefined &&
                      order.pick_pack_fee > 0 ? (
                        <span className="text-blue-600">
                          JOD {order.pick_pack_fee.toFixed(3)}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="p-3">
                      {order.services_fee !== undefined &&
                      order.services_fee > 0 ? (
                        <span className="text-purple-600">
                          JOD {order.services_fee.toFixed(3)}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="p-3">
                      JOD {order.delivery_fees.toFixed(2)}
                    </td>
                    <td className="p-3">
                      <span className="font-semibold text-green-700">
                        JOD {(order.grand_total || order.total).toFixed(3)}
                      </span>
                    </td>
                    <td className="p-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs 
                       ${
                         order.status === "shipped" ||
                         order.status === "ship_partially"
                           ? "bg-green-100 text-green-800"
                           : "bg-yellow-100 text-yellow-800"
                       }
                          
                          `}
                      >
                        {order.status === "shipped" ||
                        order.status === "ship_partially"
                          ? t("financialReports.table.paid")
                          : t("financialReports.table.pending")}
                      </span>
                    </td>
                    <td className="p-3">
                      <button
                        onClick={() => handleOrderDetails(order.id)}
                        className="text-gray-600 hover:text-gray-900"
                      >
                        <Eye size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>

        {/* Mobile Cards */}
        <div className="md:hidden divide-y">
          {isLoading
            ? [...Array(5)].map((_, index) => (
                <div key={index} className="p-4">
                  <Skeleton height={100} />
                </div>
              ))
            : orders?.data.map((order) => (
                <div key={order.id} className="p-4 hover:bg-gray-50">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="font-medium">
                        Order #{order.external_order_number || order.id}
                      </div>
                      <div className="text-sm text-gray-500">
                        {formatDate(order.created_at)}
                      </div>
                      <span
                        className={`inline-block mt-2 px-2 py-1 rounded-full text-xs ${
                          order.status === "shipped" ||
                          order.status === "ship_partially" ||
                          order.status === "handed_off"
                            ? "bg-green-100 text-green-800"
                            : "bg-yellow-100 text-yellow-800"
                        }`}
                      >
                        {order.status === "shipped" ||
                        order.status === "ship_partially" ||
                        order.status === "handed_off"
                          ? t("financialReports.table.paid")
                          : t("financialReports.table.pending")}
                      </span>
                    </div>
                    <button
                      onClick={() => handleOrderDetails(order.id)}
                      className="text-gray-600 hover:text-gray-900"
                    >
                      <Eye size={18} />
                    </button>
                  </div>

                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Order Amount:</span>
                      <span className="font-medium">
                        JOD {order.price.toFixed(2)}
                      </span>
                    </div>

                    {order.pick_pack_fee !== undefined &&
                      order.pick_pack_fee > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Pick & Pack:</span>
                          <span className="text-blue-600">
                            JOD {order.pick_pack_fee.toFixed(3)}
                          </span>
                        </div>
                      )}

                    {order.services_fee !== undefined &&
                      order.services_fee > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Services:</span>
                          <span className="text-purple-600">
                            JOD {order.services_fee.toFixed(3)}
                          </span>
                        </div>
                      )}

                    <div className="flex justify-between">
                      <span className="text-gray-600">Delivery Fee:</span>
                      <span className="font-medium">
                        JOD {order.delivery_fees.toFixed(2)}
                      </span>
                    </div>

                    <div className="flex justify-between pt-2 border-t mt-2">
                      <span className="font-semibold text-gray-800">
                        Grand Total:
                      </span>
                      <span className="font-bold text-green-700">
                        JOD {(order.grand_total || order.total).toFixed(3)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
        </div>

        {/* Pagination */}
        <div className="bg-white px-4 py-3 flex flex-col sm:flex-row items-center justify-between border-t border-gray-200">
          <div className="text-sm text-gray-500 mb-2 sm:mb-0">
            {t("financialReports.pagination.showing")} {orders?.meta.from}-
            {orders?.meta.to} {t("financialReports.pagination.of")}{" "}
            {orders?.meta.total} {t("financialReports.pagination.entries")}
          </div>
          <div className="flex space-x-2">
            <button
              className="px-3 py-1 border rounded text-sm disabled:opacity-50"
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              {t("financialReports.pagination.previous")}
            </button>
            <button className="px-3 py-1 border rounded text-sm bg-blue-600 text-white">
              {currentPage}
            </button>
            <button
              className="px-3 py-1 border rounded text-sm disabled:opacity-50"
              disabled={currentPage === orders?.meta.last_page}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              {t("financialReports.pagination.next")}
            </button>
          </div>
        </div>
      </div>

      {/* Recent Payouts */}
      <div className="bg-white rounded-lg shadow p-4">
        <h2 className="text-lg font-medium mb-4">
          {t("financialReports.recentPayouts.title")}
        </h2>
        {isLoading ? (
          [...Array(2)].map((_, index) => (
            <div
              key={index}
              className="border rounded-md p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4"
            >
              <div className="mb-2 sm:mb-0">
                <Skeleton width={120} height={20} />
                <Skeleton width={100} height={16} className="mt-1" />
              </div>
              <div className="text-right">
                <Skeleton width={80} height={20} />
                <Skeleton
                  width={60}
                  height={24}
                  className="mt-1"
                  borderRadius={12}
                />
              </div>
            </div>
          ))
        ) : payouts?.data?.length! > 0 ? (
          <div className="space-y-4">
            {payouts?.data.map((payout) => (
              <div
                key={payout.id}
                className="border rounded-md p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center"
              >
                <div className="mb-2 sm:mb-0">
                  <span className="font-medium block">
                    {t("financialReports.recentPayouts.payout")} {payout.id}
                  </span>
                  <span className="text-sm text-gray-500 block">
                    {formatDate(payout.date)}
                  </span>
                </div>
                <div className="text-right flex items-center gap-4">
                  <span className="font-medium text-lg block">
                    JOD{" "}
                    {payout?.amount?.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                  <span
                    className={`inline-flex px-2 py-1 text-xs rounded-full 
                    ${getStatusColors(payout.status).bg} ${
                      getStatusColors(payout.status).text
                    }`}
                  >
                    {payout.status.charAt(0).toUpperCase() +
                      payout.status.slice(1)}
                  </span>
                  <button
                    onClick={() => handleViewPaymentDetails(payout.id)}
                    className="text-gray-600 hover:text-gray-900"
                  >
                    <Eye size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center text-gray-500 py-4">
            {t("financialReports.recentPayouts.noPayouts")}
          </div>
        )}
      </div>
    </div>
  );
};

export default FinancialReports;
